/**
 * Portfolio Allocation View
 * Displays asset class allocation with targets and drift indicators
 */
import { usePortfolioAllocation, useSetPortfolioTargets } from '@/hooks/useAllocation';
import Card from '@/components/ui/Card';
import Button from '@/components/ui/Button';
import {
  RISK_PROFILE_DISPLAY_NAMES,
  type AssetClass,
  type AssetClassAllocation,
} from '@/types/allocation';
import { useState } from 'react';

export default function PortfolioAllocationView() {
  const { data, isLoading } = usePortfolioAllocation();
  const [isEditing, setIsEditing] = useState(false);

  if (isLoading || !data) {
    return <PortfolioSkeleton />;
  }

  const { allocation } = data;

  return (
    <div className="space-y-6">
      {/* Summary card */}
      <Card>
        <div className="p-6">
          <div className="flex items-start justify-between">
            <div>
              <h2 className="text-lg font-semibold text-gray-900">
                Portfolio Overview
              </h2>
              <p className="mt-1 text-sm text-gray-500">
                Total portfolio value and allocation summary
              </p>
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setIsEditing(!isEditing)}
            >
              {isEditing ? 'Cancel' : 'Edit Targets'}
            </Button>
          </div>

          <div className="mt-6 grid grid-cols-1 gap-6 sm:grid-cols-3">
            <div>
              <p className="text-sm font-medium text-gray-500">Total Value</p>
              <p className="mt-1 text-2xl font-semibold text-gray-900">
                ${formatNumber(allocation.totalValueUsd)}
              </p>
            </div>
            <div>
              <p className="text-sm font-medium text-gray-500">Holdings</p>
              <p className="mt-1 text-2xl font-semibold text-gray-900">
                {allocation.classifiedHoldings}
                <span className="text-sm text-gray-500 font-normal ml-1">
                  / {allocation.totalHoldings}
                </span>
              </p>
            </div>
            <div>
              <p className="text-sm font-medium text-gray-500">Risk Profile</p>
              <p className="mt-1 text-2xl font-semibold text-gray-900">
                {allocation.riskProfile
                  ? RISK_PROFILE_DISPLAY_NAMES[allocation.riskProfile]
                  : 'Not set'}
              </p>
            </div>
          </div>
        </div>
      </Card>

      {/* Allocation breakdown */}
      <Card>
        <div className="p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">
            Asset Class Breakdown
          </h3>

          {isEditing ? (
            <AllocationEditor
              allocations={allocation.allocations}
              onSave={() => setIsEditing(false)}
              onCancel={() => setIsEditing(false)}
            />
          ) : (
            <AllocationTable allocations={allocation.allocations} />
          )}
        </div>
      </Card>

      {/* Unclassified holdings warning */}
      {allocation.unclassifiedHoldings > 0 && (
        <Card className="border-yellow-200 bg-yellow-50">
          <div className="p-4 flex items-center gap-3">
            <WarningIcon className="h-5 w-5 text-yellow-600 flex-shrink-0" />
            <div>
              <p className="text-sm font-medium text-yellow-800">
                {allocation.unclassifiedHoldings} holding(s) need classification
              </p>
              <p className="text-sm text-yellow-700 mt-0.5">
                Visit the Holdings tab to classify them for accurate allocation tracking.
              </p>
            </div>
          </div>
        </Card>
      )}
    </div>
  );
}

interface AllocationTableProps {
  allocations: AssetClassAllocation[];
}

function AllocationTable({ allocations }: AllocationTableProps) {
  // Sort by current percentage descending, handling null/undefined
  const sorted = [...allocations].sort((a, b) => {
    const aVal = a.currentPercentage ? parseFloat(a.currentPercentage) : 0;
    const bVal = b.currentPercentage ? parseFloat(b.currentPercentage) : 0;
    return (Number.isNaN(bVal) ? 0 : bVal) - (Number.isNaN(aVal) ? 0 : aVal);
  });

  return (
    <div className="overflow-x-auto">
      <table className="min-w-full divide-y divide-gray-200">
        <thead>
          <tr>
            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Asset Class
            </th>
            <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
              Current
            </th>
            <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
              Target
            </th>
            <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
              Drift
            </th>
            <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
              Value
            </th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-200">
          {sorted.map((alloc) => (
            <AllocationRow key={alloc.assetClass} allocation={alloc} />
          ))}
        </tbody>
      </table>
    </div>
  );
}

interface AllocationRowProps {
  allocation: AssetClassAllocation;
}

function AllocationRow({ allocation }: AllocationRowProps) {
  // Handle null/undefined values to avoid NaN display
  const rawCurrentPct = allocation.currentPercentage
    ? parseFloat(allocation.currentPercentage)
    : 0;
  const currentPct = Number.isNaN(rawCurrentPct) ? 0 : rawCurrentPct;
  const targetPct = allocation.targetPercentage
    ? parseFloat(allocation.targetPercentage)
    : null;
  const driftPct = allocation.driftPercentage
    ? parseFloat(allocation.driftPercentage)
    : null;

  return (
    <tr className="hover:bg-gray-50">
      <td className="px-4 py-4 whitespace-nowrap">
        <div className="flex items-center gap-3">
          <div
            className="w-3 h-3 rounded-full"
            style={{ backgroundColor: getAssetClassColor(allocation.assetClass) }}
          />
          <div>
            <p className="text-sm font-medium text-gray-900">
              {allocation.displayName}
            </p>
            <p className="text-xs text-gray-500">
              {allocation.holdingCount} holding{allocation.holdingCount !== 1 ? 's' : ''}
            </p>
          </div>
        </div>
      </td>
      <td className="px-4 py-4 whitespace-nowrap text-right">
        <span className="text-sm font-medium text-gray-900">
          {currentPct.toFixed(1)}%
        </span>
      </td>
      <td className="px-4 py-4 whitespace-nowrap text-right">
        <span className="text-sm text-gray-600">
          {targetPct !== null ? `${targetPct.toFixed(1)}%` : '—'}
        </span>
      </td>
      <td className="px-4 py-4 whitespace-nowrap text-right">
        {driftPct !== null && allocation.driftDirection ? (
          <DriftBadge
            drift={driftPct}
            direction={allocation.driftDirection}
          />
        ) : (
          <span className="text-sm text-gray-400">—</span>
        )}
      </td>
      <td className="px-4 py-4 whitespace-nowrap text-right">
        <span className="text-sm text-gray-900">
          ${formatNumber(allocation.currentAmount)}
        </span>
      </td>
    </tr>
  );
}

interface DriftBadgeProps {
  drift: number;
  direction: 'over' | 'under';
}

function DriftBadge({ drift, direction }: DriftBadgeProps) {
  const isOver = direction === 'over';
  const severity = getSeverity(drift);

  const colorClasses = {
    info: 'bg-blue-100 text-blue-800',
    warning: 'bg-yellow-100 text-yellow-800',
    alert: 'bg-red-100 text-red-800',
  };

  return (
    <span
      className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${colorClasses[severity]}`}
    >
      {isOver ? '+' : '-'}{drift.toFixed(1)}%
    </span>
  );
}

function getSeverity(drift: number): 'info' | 'warning' | 'alert' {
  if (drift >= 10) return 'alert';
  if (drift >= 5) return 'warning';
  return 'info';
}

interface AllocationEditorProps {
  allocations: AssetClassAllocation[];
  onSave: () => void;
  onCancel: () => void;
}

function AllocationEditor({ allocations, onSave, onCancel }: AllocationEditorProps) {
  const { mutate: setTargets, isPending } = useSetPortfolioTargets();
  const [targets, setLocalTargets] = useState<Record<AssetClass, number>>(() => {
    const initial: Partial<Record<AssetClass, number>> = {};
    for (const alloc of allocations) {
      initial[alloc.assetClass] = alloc.targetPercentage
        ? parseFloat(alloc.targetPercentage)
        : 0;
    }
    return initial as Record<AssetClass, number>;
  });

  const total = Object.values(targets).reduce((sum, val) => sum + val, 0);
  const isValid = Math.abs(total - 100) < 0.01;

  const handleSave = () => {
    setTargets(
      { targets },
      {
        onSuccess: () => onSave(),
      }
    );
  };

  return (
    <div className="space-y-4">
      <p className="text-sm text-gray-600">
        Set target percentages for each asset class. Total must equal 100%.
      </p>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        {allocations.map((alloc) => (
          <div key={alloc.assetClass} className="flex items-center gap-3">
            <label className="flex-1 text-sm text-gray-700">
              {alloc.displayName}
            </label>
            <div className="flex items-center gap-1">
              <input
                type="number"
                min="0"
                max="100"
                step="0.1"
                value={targets[alloc.assetClass] ?? 0}
                onChange={(e) =>
                  setLocalTargets({
                    ...targets,
                    [alloc.assetClass]: parseFloat(e.target.value) || 0,
                  })
                }
                className="w-20 px-2 py-1 text-right border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
              />
              <span className="text-sm text-gray-500">%</span>
            </div>
          </div>
        ))}
      </div>

      <div className="flex items-center justify-between pt-4 border-t border-gray-200">
        <div>
          <span className={`text-sm font-medium ${isValid ? 'text-green-600' : 'text-red-600'}`}>
            Total: {total.toFixed(1)}%
          </span>
          {!isValid && (
            <span className="text-sm text-red-600 ml-2">
              (must equal 100%)
            </span>
          )}
        </div>
        <div className="flex gap-2">
          <Button variant="ghost" size="sm" onClick={onCancel}>
            Cancel
          </Button>
          <Button
            variant="primary"
            size="sm"
            onClick={handleSave}
            disabled={!isValid}
            isLoading={isPending}
          >
            Save Targets
          </Button>
        </div>
      </div>
    </div>
  );
}

function PortfolioSkeleton() {
  return (
    <div className="space-y-6">
      <Card>
        <div className="p-6 animate-pulse">
          <div className="h-6 bg-gray-200 rounded w-1/4 mb-4" />
          <div className="grid grid-cols-3 gap-6">
            <div className="h-12 bg-gray-200 rounded" />
            <div className="h-12 bg-gray-200 rounded" />
            <div className="h-12 bg-gray-200 rounded" />
          </div>
        </div>
      </Card>
      <Card>
        <div className="p-6 animate-pulse">
          <div className="h-6 bg-gray-200 rounded w-1/3 mb-4" />
          <div className="space-y-3">
            {[1, 2, 3, 4, 5].map((i) => (
              <div key={i} className="h-10 bg-gray-200 rounded" />
            ))}
          </div>
        </div>
      </Card>
    </div>
  );
}

function WarningIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      fill="none"
      viewBox="0 0 24 24"
      strokeWidth={1.5}
      stroke="currentColor"
      aria-hidden="true"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z"
      />
    </svg>
  );
}

// Asset class colors for visualization
function getAssetClassColor(assetClass: AssetClass): string {
  const colors: Record<AssetClass, string> = {
    stocks_domestic: '#3B82F6',
    stocks_international: '#6366F1',
    stocks_emerging: '#8B5CF6',
    bonds_government: '#10B981',
    bonds_corporate: '#059669',
    bonds_municipal: '#0D9488',
    bonds_international: '#14B8A6',
    real_estate: '#F59E0B',
    commodities: '#EF4444',
    crypto: '#EC4899',
    cash: '#6B7280',
    alternatives: '#A855F7',
    other: '#9CA3AF',
  };
  return colors[assetClass];
}

function formatNumber(value: string | number | null | undefined): string {
  if (value == null || value === '') return '0';
  const num = typeof value === 'string' ? parseFloat(value) : value;
  if (Number.isNaN(num)) return '0';
  return new Intl.NumberFormat('en-US', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  }).format(num);
}
